import { useEffect, useRef, useState } from "react";
import { motion, useInView, useAnimation, useReducedMotion } from "framer-motion";

interface Props {
  children: React.ReactNode;
  width?: "fit-content" | "100%";
  className?: string;
  delay?: number;
}

export const Reveal = ({ children, width = "100%", className = "", delay = 0.25 }: Props) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const mainControls = useAnimation();
  const prefersReducedMotion = useReducedMotion();
  
  // Detect mobile for performance optimization
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  useEffect(() => {
    if (isInView) {
      mainControls.start("visible");
    }
  }, [isInView, mainControls]);

  if (prefersReducedMotion) {
    return (
      <div ref={ref} className={className} style={{ position: "relative", width }}>
        {children}
      </div>
    );
  }

  // Cinematic Animation Config
  const duration = isMobile ? 0.6 : 0.8;
  const yOffset = isMobile ? 20 : 40;
  
  return (
    <div ref={ref} className={className} style={{ position: "relative", width }}>
      <motion.div
        variants={{
          hidden: { 
            opacity: 0, 
            y: yOffset 
          },
          visible: { 
            opacity: 1, 
            y: 0 
          },
        }}
        initial="hidden"
        animate={mainControls}
        transition={{ 
          duration: duration, 
          ease: [0.22, 1, 0.36, 1], 
          delay: isMobile ? delay * 0.5 : delay 
        }}
        style={{ willChange: "opacity, transform" }}
      >
        {children}
      </motion.div>
    </div>
  );
};

export default Reveal;

